/**
 * equityService.ts —— 赢率面板状态机（设计方案图 3 的 8 个状态）
 *
 * 关键约束（逐条对应 PRD 4.x / 设计方案 4.6）：
 *   - 面板**不订阅活跃对手数**：人数只作为埋点维度传入，从数据层面消除人数联动的可能（AC4.8）。
 *   - 翻牌前走查表分支，直达结果态，不经任何 loading（AC4.5）。
 *   - ≤300ms 只出 skeleton；>300ms 出真实进度；超上限只换文案与配色，绝不降级为估算（PRD 4.6）。
 *   - 四个取消触发点：关闭计算器 / 发出新公共牌 / 本手结束 / 玩家已行动（AC4.7）。
 *   - 每个节点的结果同时写入手牌快照，供复盘与手牌历史读取、不重算（AC4.9 / PRD 5.2）。
 */

import type { Card } from '../core/cards';
import type { CancelReason, EquityProgress, EquityResult, Street } from '../core/equity';
import type { EquityChannel } from './equityClient';
import { LOADING_THRESHOLD_MS, STREET_TIME_BUDGET_MS, toSnapshot, type EquitySnapshot } from './equityTypes';
import { createStore, type Store } from './store';
import { track } from './telemetry';
import type { EquityMode } from './settings';

/**
 * 慢速提示阈值。
 *
 * 设计方案图 3 写的是「LOAD → SLOW：超出耗时上限（翻牌 50ms）」，但 LOAD 本身要 >300ms 才进入，
 * 于是一进 LOAD 就立刻变黄，加载态几乎不可见。结合用户拍板的「性能提示位不常驻、仅超时才出现」，
 * 这里把**视觉**慢速提示阈值定为 800ms；而**埋点** calc_slow 仍严格按 PRD 4.6 的各街上限上报。
 * 该偏差已记入自测报告的「需设计确认项」。
 */
export const SLOW_HINT_MS = 800;

/**
 * 硬超时（看门狗）。
 *
 * 为什么必须有：改前 `dispatch()` 只有「300ms 升 loading」「800ms 升 slow」两个定时器，
 * 终结态**完全依赖通道回调**。而赢率是跑在 module worker 里的，
 * 「资产 404 / 启动脚本抛错 / worker 线程卡死」这几类故障在部分浏览器不会触发
 * `worker.onerror`，回调永远不来 —— 面板于是永久停在 slow（QA 实测 33s 无变化），
 * 既没有 error 态也没有重试入口，用户只能刷新页面（QA-BUG-022 / AC4.11）。
 *
 * 取值 4000ms 的理由（只求一个安全余量足够大的确定值，不做调参搜索）：
 *   · 真实计算上限：各街预算 5–50ms（`STREET_TIME_BUDGET_MS`），实测翻牌圈约 23ms；
 *   · 诊断构建的人为燃烧上限：3 × 400ms = 1200ms（仅 DEBUG_TOOLS 包），4000ms 仍有 3.3× 余量；
 *   · 与慢速提示的关系：5 × SLOW_HINT_MS，用户先看到「计算较慢」再看到「计算失败 + 重试」，
 *     两级提示不会互相打断，且**慢速阈值仍是 800ms，不因本次整改改变**；
 *   · 它只是兜底：正常路径永远由通道回调先终结，看门狗不参与任何视觉降级。
 */
export const HARD_TIMEOUT_MS = 4000;

export type EquityPhase =
  | 'off' // 关闭态（默认）
  | 'silent' // 静默计算（≤300ms，skeleton）
  | 'loading' // 加载态（>300ms，真实进度）
  | 'slow' // 慢速提示态（继续算，不降精度）
  | 'result' // 结果态
  | 'unavailable' // 暂不可用（离线表校验失败）
  | 'cancelled' // 已取消（保留上一有效值并置灰）
  | 'error'; // 其他错误 + 重试入口

export interface EquityPanelState {
  readonly enabled: boolean;
  readonly mode: EquityMode;
  readonly phase: EquityPhase;
  readonly street: Street | null;
  readonly result: EquityResult | null;
  readonly progress: EquityProgress | null;
  /** 已取消 / 已失效时保留的上一有效值（置灰展示，绝不当作当前值）。 */
  readonly stale: EquitySnapshot | null;
  readonly cancelReason: CancelReason | null;
  readonly message: string | null;
  /** 复盘对照的左列：本手「翻牌前」节点快照。 */
  readonly baseline: EquitySnapshot | null;
  /** 本手是否见到过翻牌（复盘态用于区分「本手未见翻牌」）。 */
  readonly sawFlop: boolean;
}

const INITIAL: EquityPanelState = {
  enabled: false,
  mode: 'live',
  phase: 'off',
  street: null,
  result: null,
  progress: null,
  stale: null,
  cancelReason: null,
  message: null,
  baseline: null,
  sawFlop: false,
};

/** 一个「节点」= 一手牌里某条街的当前牌面。 */
export interface EquityNode {
  readonly handNumber: number;
  readonly street: Street;
  readonly hole: readonly Card[];
  readonly board: readonly Card[];
  /** 仅埋点维度，不参与计算（AC4.8）。 */
  readonly activeOpponents: number;
}

export interface EquityServiceDeps {
  readonly channel: EquityChannel;
  /** 结果落到手牌快照（PRD 5.2）。 */
  readonly onSnapshot: (handNumber: number, street: Street, snap: EquitySnapshot) => void;
  /** 读取本手已有快照（复盘左列 / 已取消态回退值）。 */
  readonly getSnapshot: (handNumber: number, street: Street) => EquitySnapshot | null;
  readonly onToast?: (text: string, kind?: 'info' | 'warning' | 'error') => void;
  readonly slowBurnMs?: () => number;
  readonly now?: () => number;
}

export class EquityService {
  readonly store: Store<EquityPanelState> = createStore<EquityPanelState>(INITIAL);
  private deps: EquityServiceDeps;
  private node: EquityNode | null = null;
  private timers: ReturnType<typeof setTimeout>[] = [];
  private reqId = 0;

  constructor(deps: EquityServiceDeps) {
    this.deps = deps;
  }

  private now(): number {
    return this.deps.now ? this.deps.now() : typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  private patch(p: Partial<EquityPanelState>): void {
    this.store.set((prev) => ({ ...prev, ...p }));
  }

  private clearTimers(): void {
    for (const t of this.timers) clearTimeout(t);
    this.timers = [];
  }

  /** 面板总开关。开启后立即对当前节点起算（AC4.1）。 */
  setEnabled(on: boolean, node?: EquityNode | null): void {
    const st = this.store.get();
    if (st.enabled === on) return;
    if (on) {
      this.patch({ enabled: true, phase: 'off', stale: null, cancelReason: null, message: null });
      track('calc_open', {
        mode: st.mode,
        street: node?.street ?? null,
        active_opponents: node?.activeOpponents ?? null,
      });
      if (node) this.startNode(node);
    } else {
      const cur = this.node;
      this.cancel('calculator_closed');
      this.clearTimers();
      this.node = null;
      this.patch({ ...INITIAL, mode: st.mode, enabled: false });
      track('calc_close', { mode: st.mode, street: cur?.street ?? null, active_opponents: cur?.activeOpponents ?? null });
    }
  }

  /**
   * 从存档恢复「开关态」（REG2-NEW-003）。
   *
   * 与 setEnabled 的区别，也是为什么不能直接复用它：
   *   · 不打 calc_open 埋点 —— 这不是一次用户开启行为，只是把持久化设置回填成同一份真源；
   *   · 不在这里起算 —— 恢复流程随后会走 syncEquityNode(...) → startNode(...)，
   *     在这里抢跑会多发一次请求并把刚恢复的快照冲掉。
   */
  restoreEnabled(on: boolean): void {
    if (this.store.get().enabled === on) return;
    this.patch({ enabled: on, phase: 'off', stale: null, cancelReason: null, message: null });
  }

  setMode(mode: EquityMode, node?: EquityNode | null): void {
    const st = this.store.get();
    if (st.mode === mode) return;
    // 切换即中止进行中的计算并提示，不弹二次确认（PRD 4.7）
    const wasBusy = this.deps.channel.busy();
    if (wasBusy) this.cancel('calculator_closed');
    this.patch({ mode });
    this.deps.onToast?.(
      mode === 'review'
        ? wasBusy
          ? '已切换为复盘模式，本次计算已取消'
          : '已切换为复盘模式'
        : '已切换为实时模式',
      'info',
    );
    if (st.enabled && node) this.startNode(node);
  }

  /** 新节点（开新手 / 发出新公共牌 / 刚开启面板）。 */
  startNode(node: EquityNode): void {
    this.node = node;
    const st = this.store.get();
    const baseline = this.deps.getSnapshot(node.handNumber, 'preflop');
    const sawFlop = node.street !== 'preflop';
    if (!st.enabled) {
      // 面板关闭时仍然计算并落快照（复盘 / 手牌历史依赖，PRD 5.2），但不改变面板可见状态
      this.patch({ baseline, sawFlop });
      this.dispatch(node, false);
      return;
    }
    this.patch({
      phase: 'silent',
      street: node.street,
      result: null,
      progress: null,
      cancelReason: null,
      message: null,
      // 上一节点的结果降级为 stale：新节点算完前它不是当前值，但取消 / 失效时要能置灰回显
      stale: st.result ? toSnapshot(st.result) : st.stale,
      baseline,
      sawFlop,
    });
    this.dispatch(node, true);
  }

  private dispatch(node: EquityNode, visible: boolean): void {
    this.clearTimers();
    const startedAt = this.now();
    this.reqId += 1;
    const myReq = this.reqId;
    const budget = STREET_TIME_BUDGET_MS[node.street];

    if (visible) {
      // ≤300ms 只出 skeleton；>300ms 才升级为加载态（真实进度）
      this.timers.push(
        setTimeout(() => {
          if (myReq === this.reqId && this.store.get().phase === 'silent') this.patch({ phase: 'loading' });
        }, LOADING_THRESHOLD_MS),
      );
      this.timers.push(
        setTimeout(() => {
          if (myReq !== this.reqId) return;
          const ph = this.store.get().phase;
          if (ph === 'loading' || ph === 'silent') this.patch({ phase: 'slow' });
        }, SLOW_HINT_MS),
      );
    }

    // 本次请求是否已终结（结果 / 错误 / 取消任一先到）。用于保证取消埋点「有且仅有一条」。
    let settled = false;

    /*
     * 看门狗：无论「面板可见」与否都要挂（面板关闭时也在算并落快照，
     * 悬挂的请求会让 channel.busy() 永久为真，后续 cancel/retry 全部错乱）。
     * 触发后的动作顺序是确定性的：
     *   1. 先标记 settled，让通道随后同步回调的 cancelled 不再重复上报；
     *   2. 停通道（Worker 卡死时 terminate 是唯一可靠手段，下次 request 会重开一个）；
     *   3. 自增 reqId，迟到的结果不许写 UI；
     *   4. 落 error 态 —— 面板的 error 态自带「重试」按钮，retry() 会用同一节点重算。
     */
    this.timers.push(
      setTimeout(() => {
        if (myReq !== this.reqId || settled) return;
        settled = true;
        this.clearTimers();
        this.deps.channel.cancel('superseded');
        this.reqId += 1;
        track('calc_error', {
          reason: 'timeout',
          street: node.street,
          elapsed_ms: Math.round((this.now() - startedAt) * 100) / 100,
          timeout_ms: HARD_TIMEOUT_MS,
          panel_open: visible,
        });
        if (visible) {
          this.patch({
            phase: 'error',
            result: null,
            progress: null,
            message: `赢率计算超时（超过 ${Math.round(HARD_TIMEOUT_MS / 1000)}s 无响应），计算线程可能未能启动。`,
          });
        }
      }, HARD_TIMEOUT_MS),
    );

    this.deps.channel.request(
      node.hole,
      node.board,
      {
        onProgress: (p) => {
          if (myReq !== this.reqId || !visible) return;
          // 真实进度先于 300ms 定时器抵达（主线程被占用而回调排队时会这样），
          // 就直接按「已越过加载阈值」升级，避免出现「有 loading 但进度恒 0」的假进度。
          if (this.store.get().phase === 'silent' && p.elapsedMs >= LOADING_THRESHOLD_MS) {
            this.patch({ phase: 'loading', progress: p });
            return;
          }
          this.patch({ progress: p });
        },
        onResult: (r) => {
          if (myReq !== this.reqId) return;
          settled = true;
          this.clearTimers();
          this.deps.onSnapshot(node.handNumber, node.street, toSnapshot(r));
          if (visible) {
            this.patch({ phase: 'result', result: r, progress: null, stale: null, cancelReason: null, message: null });
          }
          track('calc_result', {
            street: r.street,
            elapsed_ms: Math.round(r.elapsedMs * 100) / 100,
            hit_table: r.fromTable,
            exact: r.exact,
            boards: r.boardsEnumerated,
            panel_open: visible,
            active_opponents: node.activeOpponents,
          });
          if (r.elapsedMs > budget) {
            // 超出 PRD 4.6 上限：只上报告警，不改变结果口径（无降级路径）
            track('calc_slow', {
              street: r.street,
              elapsed_ms: Math.round(r.elapsedMs * 100) / 100,
              budget_ms: budget,
              opponents: node.activeOpponents,
              device_tier: deviceTier(),
            });
          }
        },
        onError: (kind, message, detail) => {
          if (kind === 'cancelled') {
            // 取消埋点必须无条件可达（P-03 回归）：
            // 取消可能由 cancel() 触发，也可能由通道内部的 superseded 触发，
            // 两条路径都会在 reqId 递增之后才回调，若先做 reqId 守卫，calc_cancel 永远打不出来。
            if (settled) return;
            settled = true;
            if (myReq === this.reqId) this.clearTimers();
            track('calc_cancel', {
              reason: detail ?? 'superseded',
              elapsed_ms: Math.round((this.now() - startedAt) * 100) / 100,
              street: node.street,
            });
            // 面板状态由 cancel() / startNode() 统一落地，这里不写 UI
            return;
          }
          if (myReq !== this.reqId) return;
          settled = true;
          this.clearTimers();
          if (kind === 'unavailable') {
            track('calc_unavailable', { reason: detail ?? message, street: node.street, table_version: 1 });
            if (visible) this.patch({ phase: 'unavailable', result: null, progress: null, message: detail ?? message });
            return;
          }
          track('calc_error', {
            reason: detail ?? 'failed',
            street: node.street,
            elapsed_ms: Math.round((this.now() - startedAt) * 100) / 100,
            timeout_ms: null,
            panel_open: visible,
          });
          if (visible) this.patch({ phase: 'error', result: null, progress: null, message });
        },
      },
      this.deps.slowBurnMs?.() || undefined,
    );
  }

  /**
   * 四个取消触发点统一入口（AC4.7）。
   *
   * 时序是确定性的，不依赖任何竞态：
   *   1. 先让通道停止计算（Worker 软取消 + 硬终止），通道会同步回调 onError('cancelled')，
   *      埋点因此必达（P-03：此前先自增 reqId 会把该回调丢掉）；
   *   2. 再自增 reqId，让此后任何迟到的结果 / 进度都无法写入 UI；
   *   3. 最后落地面板状态（保留上一有效值并置灰）。
   */
  cancel(reason: CancelReason): void {
    if (!this.deps.channel.busy()) return;
    const st = this.store.get();
    this.clearTimers();
    this.deps.channel.cancel(reason); // 同步停止：返回后 channel.busy() 必为 false
    this.reqId += 1; // 让后到的回调全部失效，绝不允许旧值写入 UI
    if (!st.enabled) return;
    if (reason === 'calculator_closed') return; // 关闭时整块移除，由 setEnabled 处理
    const fallback = st.result ? toSnapshot(st.result) : st.stale;
    this.patch({ phase: 'cancelled', result: null, progress: null, stale: fallback, cancelReason: reason });
  }

  /** 玩家提交动作（取消触发点之一）。 */
  onPlayerActed(): void {
    this.cancel('player_acted');
  }

  /** 本手结束（取消触发点之一）。 */
  onHandEnded(): void {
    this.cancel('hand_ended');
  }

  /** 我方被淘汰 / 观战：口径依赖我的底牌，观战时无输入，面板自动关闭。 */
  forceOff(): void {
    this.setEnabled(false);
  }

  retry(): void {
    if (this.node) this.startNode(this.node);
  }

  dispose(): void {
    this.clearTimers();
    this.deps.channel.dispose();
  }
}

function deviceTier(): string {
  if (typeof navigator === 'undefined') return 'node';
  const cores = (navigator as unknown as { hardwareConcurrency?: number }).hardwareConcurrency ?? 0;
  if (cores >= 8) return 'high';
  if (cores >= 4) return 'mid';
  return 'low';
}
